import { useState } from 'react';
import { Icons } from '../../ui/Icons';
import { ChurchInviteConfig, ChurchRsvpResponse } from '../../../types';

interface ChurchInviteCardProps {
  config: ChurchInviteConfig;
  onSubmit: (response: ChurchRsvpResponse) => void;
  isSubmitting: boolean;
}

export const ChurchInviteCard = ({ config, onSubmit, isSubmitting }: ChurchInviteCardProps) => {
  const [showRsvpForm, setShowRsvpForm] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [rsvpStatus, setRsvpStatus] = useState<'yes' | 'maybe' | 'no'>('yes');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<{ name?: string }>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setErrors({ name: 'Name is required' });
      return;
    }

    onSubmit({
      type: 'church_invite',
      name: name.trim(),
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      rsvpStatus,
      notes: notes.trim() || undefined
    });
  };

  return (
    <div className="space-y-4">
      {/* Church Info Card */}
      <div className="bg-gray-50 rounded-lg p-4">
        {config.coverImage && (
          <img
            src={config.coverImage}
            alt={config.churchName}
            className="w-full h-40 object-cover rounded-lg mb-4"
          />
        )}

        <h3 className="text-xl font-bold text-gray-800 mb-3">{config.churchName}</h3>

        <div className="space-y-2 text-sm text-gray-600">
          {config.address && (
            <div className="flex items-start gap-2">
              <Icons.Map className="w-4 h-4 mt-0.5 text-gray-400" />
              <span>{config.address}</span>
            </div>
          )}
          {config.serviceTime && (
            <div className="flex items-center gap-2">
              <Icons.Clock className="w-4 h-4 text-gray-400" />
              <span>{config.serviceTime}</span>
            </div>
          )}
          {config.contactEmail && (
            <div className="flex items-center gap-2">
              <Icons.Mail className="w-4 h-4 text-gray-400" />
              <a href={`mailto:${config.contactEmail}`} className="text-primary hover:underline">
                {config.contactEmail}
              </a>
            </div>
          )}
          {config.contactPhone && (
            <div className="flex items-center gap-2">
              <Icons.User className="w-4 h-4 text-gray-400" />
              <a href={`tel:${config.contactPhone}`} className="text-primary hover:underline">
                {config.contactPhone}
              </a>
            </div>
          )}
        </div>

        {config.mapLink && (
          <a
            href={config.mapLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-3 text-primary hover:underline text-sm"
          >
            <Icons.Map className="w-4 h-4" />
            View on Map
          </a>
        )}
      </div>

      {/* RSVP Section */}
      {config.collectRsvp && (
        <>
          {!showRsvpForm ? (
            <button
              onClick={() => setShowRsvpForm(true)}
              className="w-full py-3 bg-primary text-white font-bold rounded-lg hover:bg-blue-700 transition-colors"
            >
              I'd Like to Visit
            </button>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 border-t pt-4">
              <h4 className="font-medium text-gray-800">Let us know you're coming!</h4>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name) setErrors({});
                  }}
                  placeholder="Enter your name"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary text-gray-900 ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email (optional)
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary text-gray-900"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Will you be visiting?
                </label>
                <div className="flex gap-2">
                  {(['yes', 'maybe', 'no'] as const).map((status) => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => setRsvpStatus(status)}
                      className={`flex-1 py-2 px-3 rounded-lg border font-medium capitalize transition-colors ${
                        rsvpStatus === status
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-primary'
                      }`}
                    >
                      {status === 'yes' ? 'Yes!' : status === 'maybe' ? 'Maybe' : 'Not yet'}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 bg-primary text-white font-bold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Icons.Loader className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit RSVP'
                )}
              </button>
            </form>
          )}
        </>
      )}
    </div>
  );
};
